import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2 } from 'lucide-react';

import { Button } from 'components/ui/button';
import { ScrollArea } from 'components/ui/scroll-area';
import { cn } from 'lib/utils';
import { getQuestionInfo } from './data';
import { useQuestionnaireStore } from 'store/questionnaire';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Checkbox } from 'components/ui/checkbox';
import { getQuestionnaireV3 } from 'services/profile/api';
import { useState } from 'react';
import useRiskCheckerStore from 'store/risk-checker';
import { QUESTIONNAIRE_CODE } from 'lib/constants';

interface QuestionnaireWrapperProps {
  children: React.ReactNode;
}

const noSidebar: string[] = [];

export default function QuestionnaireWrapper({
  children,
}: QuestionnaireWrapperProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');

  const { t } = useTranslation();

  const engineName = useRiskCheckerStore((store) => store.engineName);
  const questionData = useQuestionnaireStore((store) => store.questionData);
  const setQuestionData = useQuestionnaireStore(
    (store) => store.setQuestionData
  );

  const [isLoading, setIsLoading] = useState(false);

  const onBack = async () => {
    if (code) {
      return navigate(-1);
    }

    setIsLoading(true);
    const response: any = await getQuestionnaireV3({
      retrieve_type: 'previous',
      question_sequence: questionData?.question_sequence || 1,
    });
    if (!response?.data) {
      setQuestionData(undefined);
      return navigate(-1);
    }

    if (response?.data?.length && response?.data[0]) {
      const nextQuestion = response?.data[0];
      setQuestionData(nextQuestion);
      setIsLoading(false);
    }
  };

  const questionInfo = getQuestionInfo(engineName || 'hypertension_risk');

  const questionSequence =
    (questionData?.code
      ? questionInfo.questionNumber[questionData.code as QUESTIONNAIRE_CODE]
      : 0) || 0;

  return (
    <div className='p-4 space-y-4'>
      <Button
        variant='outline'
        className='h-8 px-6 text-sm rounded-full text-primary hover:text-primary border-primary'
        onClick={onBack}
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 className='w-4 h-4 mb-px mr-1 animate-spin' />
        ) : (
          <ArrowLeft className='w-4 h-4 mb-px mr-1' />
        )}{' '}
        {t('go_back')}
      </Button>

      <div className='pt-4 pb-2 border rounded-md shadow-sm'>
        <div className='px-4 pb-2 space-y-2 border-b'>
          <div className='flex items-center gap-2'>
            <div className='flex items-center justify-center text-sm rounded-full size-5 bg-primary/10'>
              <p className='font-semibold text-primary'>{questionSequence}</p>
            </div>
            <h2 className='font-semibold'>
              {questionData?.code
                ? questionInfo.questionTitle[
                    questionData.code as QUESTIONNAIRE_CODE
                  ]
                : ''}
            </h2>
          </div>
          <div className='space-y-1'>
            <div className='grid grid-cols-4 gap-1'>
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index.toString()}
                  className={cn(
                    'bg-[#E6E6E6] w-full h-2 rounded-full',
                    questionSequence > index + 1 && 'bg-primary'
                  )}
                />
              ))}
            </div>
            <p>{questionSequence - 1} of 4 questionnaire completed</p>
          </div>
        </div>
        <div className='grid grid-cols-12'>
          <div className='col-span-3'>
            <ScrollArea className='h-[calc(100vh-18rem)] border-r'>
              <div>
                {Object.entries(questionInfo.sideQuestions).map(
                  ([key, value]) => {
                    if (noSidebar.includes(key as QUESTIONNAIRE_CODE))
                      return null;

                    return (
                      <div
                        key={key}
                        className='flex items-center gap-2 p-4 border-b'
                      >
                        <Checkbox
                          id={key}
                          checked={value.sequence < questionSequence}
                          disabled
                        />
                        <h2
                          className={cn(
                            'text-sm font-semibold',
                            value.sequence < questionSequence && 'text-primary'
                          )}
                        >
                          {value.label}
                        </h2>
                      </div>
                    );
                  }
                )}
              </div>
            </ScrollArea>
          </div>
          <div className='col-span-9'>{children}</div>
        </div>
      </div>
    </div>
  );
}
